import { 
  Card, 
  CardHeader, 
  CardContent, 
  Typography, 
  IconButton,
  Box,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { ResponsiveBar } from '@nivo/bar';
import { chartTheme } from './chartTheme';
import { useState, useMemo, useRef, useEffect } from 'react';
import { PRIMARY_CHART_COLORS, getDistinctColors, getDistributionColors, DISTRIBUTION_CHART_COLORS } from './colorUtils';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { useSelector } from 'react-redux';

interface ChannelData {
  name: string;
  value: number;
  conversion_rate: number;
  converted_leads: number;
}

interface ProductData {
  name: string;
  total: number;
  channels: ChannelData[];
}

interface ChannelProductItem {
  channel: string;
  total_leads: string;
  converted_leads: string;
  conversion_rate: string;
  contribution_rate?: string;
  products: {
    [key: string]: {
      product_group: string | null;
      total_leads: string;
      converted_leads: string;
      conversion_rate: string;
    }
  }
}

interface ProductInChannel {
  product_group: string | null;
  total_leads: string;
  converted_leads: string;
  conversion_rate: string;
}

interface FormattedProductData {
  product: string;
  total: number;
  [key: string]: string | number;
}

interface FormattedChannelData {
  channel: string;
  total: number;
  [key: string]: string | number;
}

interface ChartData {
  name: string;
  total: number;
  conversion_rate: number;
  converted_leads: number;
  channels?: { 
    name: string; 
    value: number;
    conversion_rate: number;
    converted_leads: number;
  }[];
  statuses?: { 
    name: string; 
    value: number;
    conversion_rate: number;
    converted_leads: number;
  }[];
}

export interface BarChartProps {
  title?: string;
  data: ChartData[];
  onBarClick?: (item: string, type: 'product' | 'channel' | 'status') => void;
  compact?: boolean;
  ultraCompact?: boolean;
  showToggle?: boolean;
  showXAxis?: boolean;
}

// Helper function to truncate text
const truncateText = (text: string | null | undefined, maxLength: number): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

// Custom hook for tracking hover state
const useHover = () => {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [hoverPosition, setHoverPosition] = useState<{x: number, y: number} | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  
  const handleMouseEnter = (item: string, x: number, y: number) => {
    // Clear any existing timeout to prevent state conflicts
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // If hovering a different item, update immediately
    if (hoveredItem !== item) {
      setHoveredItem(item);
      setHoverPosition({x, y});
    }
    
    // Make visible in all cases
    setIsVisible(true);
  };
  
  const handleMouseLeave = () => {
    setIsVisible(false);
    
    // Clear any existing timeout
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }
    
    // Set a new timeout to clear the item after the animation completes
    timeoutRef.current = window.setTimeout(() => {
      setHoveredItem(null);
      setHoverPosition(null);
      timeoutRef.current = null;
    }, 300);
  };
  
  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return { hoveredItem, hoverPosition, isVisible, handleMouseEnter, handleMouseLeave };
};

export const BarChart = ({ 
  title, 
  data, 
  onBarClick, 
  compact = false, 
  ultraCompact = false,
  showToggle = true,
  showXAxis = false
}: BarChartProps) => {
  const [viewBy, setViewBy] = useState<'product' | 'channel' | 'status'>('product');
  const theme = useTheme();
  
  // Get channelProductDistribution from Redux
  const channelProductDistribution = useSelector((state: any) => state.dashboard.channelProductDistribution);

  // Add hover state for x-axis labels
  const { hoveredItem, hoverPosition, isVisible, handleMouseEnter, handleMouseLeave } = useHover();

  const handleViewChange = (event: React.MouseEvent<HTMLElement>, newView: 'product' | 'channel' | 'status' | null) => {
    if (newView !== null) {
      setViewBy(newView);
    }
  };

  const handleClick = (bar: any) => {
    if (onBarClick) {
      if (viewBy === 'product') {
        onBarClick(bar.data.product, 'product');
      } else if (viewBy === 'channel') {
        onBarClick(bar.data.channel, 'channel');
      } else {
        onBarClick(bar.data.status, 'status');
      }
    }
  };

  const formatData = () => {
    if (viewBy === 'product') {
      return data.map(item => {
        const result: any = { product: item.name };
        if (item.channels) {
          item.channels.forEach(channel => {
            result[channel.name] = channel.value;
          });
        } else if (item.statuses) {
          item.statuses.forEach(status => {
            result[status.name] = status.value;
          });
        }
        return result;
      });
    } else if (viewBy === 'channel') {
      // Use channelProductDistribution from Redux if available
      if (channelProductDistribution?.channels) {
        return channelProductDistribution.channels.map((channelItem: ChannelProductItem) => {
          const result: Record<string, any> = { 
            channel: channelItem.channel,
            total_leads: Number(channelItem.total_leads) || 0,
            converted_leads: Number(channelItem.converted_leads) || 0,
            conversion_rate: Number(channelItem.conversion_rate) || 0,
            contribution_rate: Number(channelItem.contribution_rate) || 0,
          };
          
          // Add products to result
          if (channelItem.products) {
            Object.entries(channelItem.products).forEach(([productName, productData]) => {
              // Use product group name if available, otherwise use key
              const name = productData.product_group || productName;
              result[name] = Number(productData.total_leads) || 0;
            });
          }
          
          return result;
        });
      }
      
      // Fallback to original implementation
      const channelMap = new Map<string, { [key: string]: number }>();
      data.forEach(item => {
        if (item.channels) {
          item.channels.forEach(channel => {
            if (!channelMap.has(channel.name)) {
              channelMap.set(channel.name, {});
            }
            const channelData = channelMap.get(channel.name)!;
            channelData[item.name] = channel.value;
          });
        }
      });
      return Array.from(channelMap.entries()).map(([channel, products]) => ({
        channel,
        ...products
      }));
    } else {
      // Transform data for status view
      const statusMap = new Map<string, { [key: string]: number }>();
      data.forEach(item => {
        if (item.statuses) {
          item.statuses.forEach(status => {
            if (!statusMap.has(status.name)) {
              statusMap.set(status.name, {});
            }
            const statusData = statusMap.get(status.name)!;
            statusData[item.name] = status.value;
          });
        }
      });
      return Array.from(statusMap.entries()).map(([status, products]) => ({
        status,
        ...products
      }));
    }
  };

  const getKeys = () => {
    if (viewBy === 'product') {
      // Get unique channels or statuses from all products
      const keys = new Set<string>();
      data.forEach(item => {
        if (item.channels) {
          item.channels.forEach(channel => {
            keys.add(channel.name);
          });
        } else if (item.statuses) {
          item.statuses.forEach(status => {
            keys.add(status.name);
          });
        }
      });
      return Array.from(keys);
    } else if (viewBy === 'channel') {
      // For channel view, get product names from channelProductDistribution if available
      if (channelProductDistribution?.channels) {
        const productKeys = new Set<string>();
        
        channelProductDistribution.channels.forEach((channel: ChannelProductItem) => {
          if (channel.products) {
            Object.entries(channel.products).forEach(([productKey, product]) => {
              // Use product_group if available, otherwise use the key
              const productName = product.product_group !== null ? product.product_group : productKey;
              if (productName) productKeys.add(productName);
            });
          }
        });
        
        return Array.from(productKeys);
      }
      
      // Fallback to original implementation
      return data.map(item => item.name);
    } else {
      // Get all product names for status view
      return data.map(item => item.name);
    }
  };

  const getChannels = () => {
    const channelSet = new Set<string>();
    data.forEach(product => {
      if (product.channels) {
        product.channels.forEach(channel => {
          channelSet.add(channel.name);
        });
      }
    });
    return Array.from(channelSet);
  };

  // Generate a set of distinct colors for the chart - using direct array access for no repetition
  const chartColors = useMemo(() => {
    const keys = getKeys();
    return DISTRIBUTION_CHART_COLORS.slice(0, keys.length);
  }, [viewBy, data]);

  // Get color for a specific channel or product based on its index
  const getItemColor = (id: string): string => {
    const keys = getKeys();
    const index = keys.indexOf(id);
    return chartColors[index % chartColors.length];
  };

  const handleExport = () => {
    try {
      const channels = getChannels();
      const csvContent = [
        ['Product', 'Total', ...channels].join(','),
        ...formatData().map((row: Record<string, any>) => [
          row.product || '',
          row.total || 0,
          ...channels.map(channel => (row as Record<string, number | string>)[channel] || 0)
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${title?.toLowerCase().replace(/\s+/g, '_') || 'bar_chart'}.csv`;
      link.click();
    } catch (error) {
      console.error('Error exporting data:', error);
    }
  };

  // Calculate the percentage contribution of each bar
  const calculatePercentages = () => {
    const formattedData = formatData();
    const keys = getKeys();
    const totals: Record<string, number> = {};
    
    // Calculate the total sum for percentage calculation
    let grandTotal = 0;
    
    // For product view, use existing data
    if (viewBy === 'product') {
      grandTotal = data.reduce((sum, item) => sum + Number(item.total), 0);
      
      formattedData.forEach((item: Record<string, any>) => {
        const productTotal = data.find(d => d.name === item.product)?.total || 0;
        totals[item.product] = (Number(productTotal) / grandTotal) * 100;
      });
    } else if (viewBy === 'channel' && channelProductDistribution?.channels) {
      // For channel view using Redux data
      grandTotal = channelProductDistribution.channels.reduce((sum: number, channel: ChannelProductItem) => 
        sum + Number(channel.total_leads), 0);
      
      channelProductDistribution.channels.forEach((channel: ChannelProductItem) => {
        totals[channel.channel] = (Number(channel.total_leads) / grandTotal) * 100;
      });
    } else if (viewBy === 'channel') {
      // Fallback to original implementation
      const channelTotals: Record<string, number> = {};
      data.forEach(product => {
        if (product.channels) {
          product.channels.forEach(channel => {
            if (!channelTotals[channel.name]) channelTotals[channel.name] = 0;
            channelTotals[channel.name] += channel.value;
          });
        }
      });
      
      grandTotal = Object.values(channelTotals).reduce((sum, val) => sum + val, 0);
      
      Object.keys(channelTotals).forEach(channel => {
        totals[channel] = (channelTotals[channel] / grandTotal) * 100;
      });
    } else {
      // For status view, calculate percentage of each status to grand total
      const statusTotals: Record<string, number> = {};
      data.forEach(product => {
        if (product.statuses) {
          product.statuses.forEach(status => {
            if (!statusTotals[status.name]) statusTotals[status.name] = 0;
            statusTotals[status.name] += status.value;
          });
        }
      });
      
      grandTotal = Object.values(statusTotals).reduce((sum, val) => sum + val, 0);
      
      Object.keys(statusTotals).forEach(status => {
        totals[status] = (statusTotals[status] / grandTotal) * 100;
      });
    }
    
    return totals;
  };

  const formatTooltipContent = (id: string | number, indexValue: string | number, value: number | null | undefined, data: any) => {
    const color = getItemColor(String(id));
    const percentages = calculatePercentages();
    const safeValue = value || 0;
    
    // Get conversion rate and converted leads from the data
    let conversionRate = 0;
    let convertedLeads = 0;
    let contributionRate = 0; 
    
    if (viewBy === 'product') {
      const product = data.find((item: any) => item.name === indexValue);
      if (product?.channels) {
        const channel = product.channels.find((c: any) => c.name === id);
        if (channel) {
          conversionRate = channel.conversion_rate || 0;
          convertedLeads = channel.converted_leads || 0;
          contributionRate = channel.contribution_rate || 0;
        }
      } else if (product?.statuses) {
        // For Product Status Distribution
        const status = product.statuses.find((s: any) => {
          return s.name === id;
      });
        if (status) {
          conversionRate = status.conversion_rate || 0;
          convertedLeads = status.converted_leads || 0;
          contributionRate = status.contribution_rate || 0;
        }
      }
    } else if (viewBy === 'channel' && channelProductDistribution?.channels) {
      // For channel view using channelProductDistribution
      const channelData = channelProductDistribution.channels.find(
        (c: ChannelProductItem) => c.channel === indexValue
      );
      
      if (channelData) {

        // For overall channel metrics
        conversionRate = Number(channelData.conversion_rate) || 0;
        convertedLeads = Number(channelData.converted_leads) || 0;
        contributionRate = Number(channelData.contribution_rate) || 0;

        
        // If looking at a specific product within the channel
        if (channelData.products && typeof indexValue === 'string') {
          // Type-safe approach to find a product
          const productKey = Object.keys(channelData.products).find(
            key => channelData.products[key].product_group === id || key === id
          );
          
          if (productKey && channelData.products[productKey]) {
            const productData = channelData.products[productKey];
            conversionRate = Number(productData.conversion_rate) || 0;
            convertedLeads = Number(productData.converted_leads) || 0;
            contributionRate = Number(productData.contribution_rate) || 0;
          }
        }
      }
    } else if (viewBy === 'channel') {
      // Fallback to original implementation
      const channelData = data.find((item: any) => item.name === id);
      if (channelData) {
        conversionRate = Number(channelData.conversion_rate) || 0;
        convertedLeads = Number(channelData.converted_leads) || 0;
        contributionRate = Number(channelData.contribution_rate) || 0;
      }
    }
    
    // Get the contribution percentage of the parent item (product or channel)
    const contributionPercentage = percentages[viewBy === 'product' ? String(indexValue) : String(id)] || 0;
    
    return (
      <div style={{
        background: 'white',
        padding: '12px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        fontSize: '14px',
        maxWidth: '280px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '8px',
          gap: '8px'
        }}>
          <div style={{
            width: '12px',
            height: '12px',
            backgroundColor: color,
            borderRadius: '2px'
          }} />
          <strong style={{ color }}>
            {id}
          </strong>
        </div>
        <div style={{ 
          display: 'flex',
          flexDirection: 'column',
          gap: '4px'
        }}>
          <div style={{ 
            display: 'flex',
            justifyContent: 'space-between',
            gap: '16px'
          }}>
            <span style={{ color: '#666' }}>Total Base:</span>
            <strong>{(safeValue || 0).toLocaleString()}</strong>
          </div>
          
          {convertedLeads > 0 && (
            <div style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              gap: '16px'
            }}>
              <span style={{ color: '#666' }}>Converted NOP:</span>
              <strong>{convertedLeads.toLocaleString()}</strong>
            </div>
          )}
          
          {conversionRate > 0 && (
            <div style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              gap: '16px'
            }}>
              <span style={{ color: '#666' }}>Conversion %:</span>
              <strong>{conversionRate}%</strong>
            </div>
          )}

          {contributionRate > 0 && (
            <div style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              gap: '16px'
            }}>
              <span style={{ color: '#666' }}>Base Contribution:</span>
              <strong>{contributionRate}%</strong>
            </div>
          )}
          
          {/* {viewBy === 'product' && (
            <div style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              gap: '16px'
            }}>
              <span style={{ color: '#666' }}>{viewBy === 'product' ? 'Product:' : 'Channel:'}</span>
              <strong>{indexValue}</strong>
            </div>
          )} */}
          
          {/* <div style={{ 
            display: 'flex',
            justifyContent: 'space-between',
            gap: '16px',
            borderTop: '1px solid #eee',
            marginTop: '4px',
            paddingTop: '4px'
          }}>
            <span style={{ color: '#666' }}>Contribution:</span>
            <strong>{(percentages[indexValue] || 0).toFixed(1)}%</strong>
          </div> */}
        </div>
      </div>
    );
  };

  // Calculate totals for each key for the legend
  const calculateTotals = () => {
    const keys = getKeys();
    const totals: Record<string, number> = {};
    
    keys.forEach(key => {
      if (viewBy === 'product') {
        // Sum values for this channel or status across all products
        totals[key] = data.reduce((sum, product) => {
          if (product.channels) {
            const channelData = product.channels.find(c => c.name === key);
            return sum + (channelData?.value || 0);
          } else if (product.statuses) {
            const statusData = product.statuses.find(s => s.name === key);
            return sum + (statusData?.value || 0);
          }
          return sum;
        }, 0);
      } else {
        // Sum values for this product across all channels or statuses
        const productData = data.find(p => p.name === key);
        if (productData?.channels) {
          totals[key] = productData.channels.reduce((sum, c) => sum + c.value, 0);
        } else if (productData?.statuses) {
          totals[key] = productData.statuses.reduce((sum, s) => sum + s.value, 0);
        } else {
          totals[key] = 0;
        }
      }
    });
    
    return totals;
  };

  // Format the metrics for a specific product or channel
  const getMetricsForItem = (itemName: string) => {
    // Initialize with default values
    let totalBase = 0;
    let convertedLeads = 0;
    let conversionRate = 0;
    let contributionRate = 0;
    if (viewBy === 'product') {
      const product = data.find(item => item.name === itemName);
      if (product) {
        totalBase = Number(product.total) || 0;
        
        // First check if we have direct conversion data on the product
        if (product.converted_leads !== undefined && product.conversion_rate !== undefined) {
          convertedLeads = Number(product.converted_leads) || 0;
          conversionRate = Number(product.conversion_rate) || 0;
        }
        // If we have statuses, extract converted leads from CONVERTED status
        else if (product.statuses) {
          const convertedStatus = product.statuses.find(status => status.name === 'CONVERTED');
          convertedLeads = Number(convertedStatus?.value) || 0;
          conversionRate = totalBase > 0 ? (convertedLeads / totalBase) * 100 : 0;
        }
        // If we have channels, sum up the converted leads from all channels
        else if (product.channels) {
          convertedLeads = product.channels.reduce((sum, channel) => 
            sum + (Number(channel.converted_leads) || 0), 0);
          conversionRate = totalBase > 0 ? (convertedLeads / totalBase) * 100 : 0;
        }
      }
    } else if (viewBy === 'channel' && channelProductDistribution?.channels) {
      const channelData = channelProductDistribution.channels.find(
        (c: ChannelProductItem) => c.channel === itemName
      );
      
      if (channelData) {
        totalBase = Number(channelData.total_leads) || 0;
        convertedLeads = Number(channelData.converted_leads) || 0;
        conversionRate = Number(channelData.conversion_rate) || 0;
        contributionRate = Number(channelData.contribution_rate) || 0;
      }
    } else if (viewBy === 'channel') {
      const channelData = data.find(item => item.name === itemName);
      if (channelData) {
        // Calculate metrics by summing up values from all products for this channel
        data.forEach(product => {
          if (product.channels) {
            const channelInProduct = product.channels.find(c => c.name === channelData.name);
            if (channelInProduct) {
              totalBase += Number(channelInProduct.value) || 0;
              convertedLeads += Number(channelInProduct.converted_leads) || 0;
            }
          }
        });
        
        conversionRate = totalBase > 0 ? (convertedLeads / totalBase) * 100 : 0;
      }
    }
    
    return {
      base: totalBase,
      converted: convertedLeads,
      rate: conversionRate
    };
  };

  return (
    <Card sx={{ 
      height: ultraCompact ? '100%' : compact ? 'auto' : 'auto',
      boxShadow: ultraCompact ? 'none' : compact ? '0 2px 8px rgba(0, 0, 0, 0.08)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
      border: ultraCompact ? 'none' : compact ? '1px solid rgba(0, 0, 0, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
      borderRadius: 2,
      overflow: 'visible',
      position: 'relative'
    }}>
      {!compact && !ultraCompact && title && (
        <CardHeader
          title={
            <Typography 
              variant="h6" 
              component="h2"
              sx={{ 
                fontWeight: 700, 
                color: theme.palette.text.primary,
                fontSize: '1.25rem',
                letterSpacing: '0.01em',
                lineHeight: 1.3
              }}
            >
              {title}
            </Typography>
          }
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {showToggle && (
                <ToggleButtonGroup
                  value={viewBy}
                  exclusive
                  onChange={handleViewChange}
                  size="small"
                  sx={{ 
                    '& .MuiToggleButton-root': {
                      px: 2,
                      py: 0.5,
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#475569',
                      borderColor: 'rgba(0, 0, 0, 0.12)',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(54, 162, 235, 0.15)',
                        color: '#36A2EB',
                        '&:hover': {
                          backgroundColor: 'rgba(54, 162, 235, 0.25)',
                        },
                      },
                    },
                    '& .MuiToggleButtonGroup-grouped:not(:last-of-type)': {
                      borderRadius: '4px 0 0 4px',
                    },
                    '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
                      borderRadius: '0 4px 4px 0',
                    },
                  }}
                >
                  <ToggleButton 
                    value="product"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    By Product
                  </ToggleButton>
                  {data[0]?.channels && (
                    <ToggleButton 
                      value="channel"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      By Channel
                    </ToggleButton>
                  )}
                  {data[0]?.statuses && (
                    <ToggleButton 
                      value="status"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      By Status
                    </ToggleButton>
                  )}
                </ToggleButtonGroup>
              )}
              <IconButton 
                onClick={handleExport}
                size="small"
                aria-label="Export data"
                title="Export data as CSV"
                sx={{ 
                  color: '#36A2EB',
                  '&:hover': {
                    backgroundColor: 'rgba(54, 162, 235, 0.1)'
                  }
                }}
              >
                <FileDownloadIcon fontSize="small" />
              </IconButton>
            </Box>
          }
          sx={{
            '& .MuiCardHeader-action': {
              margin: 0,
              alignSelf: 'center',
            },
          }}
        />
      )}
      <CardContent sx={{ 
        height: ultraCompact ? 130 : compact ? 300 : 450, 
        p: ultraCompact ? 0.5 : compact ? 1 : 2,
        '&:last-child': {
          pb: ultraCompact ? 0.5 : compact ? 1 : 2
        },
        position: 'relative' // Add this for absolute positioning of the tooltip
      }}>
        <ResponsiveBar
          data={formatData()}
          keys={getKeys()}
          indexBy={viewBy === 'product' ? 'product' : viewBy === 'channel' ? 'channel' : 'status'}
          margin={{ 
            top: ultraCompact ? 5 : compact ? 10 : 20, 
            right: ultraCompact ? 10 : compact ? 20 : 160, 
            bottom: (ultraCompact && showXAxis) ? 50 : ultraCompact ? 35 : compact ? 60 : 120, 
            left: ultraCompact ? 30 : compact ? 40 : 60 
          }}
          padding={ultraCompact ? 0.05 : compact ? 0.1 : 0.2}
          valueScale={{ type: 'linear' }}
          indexScale={{ type: 'band', round: true }}
          colors={chartColors}
          borderRadius={4}
          borderWidth={0}
          borderColor={{
            from: 'color',
            modifiers: [['darker', 0.6]]
          }}
          enableLabel={!ultraCompact}
          labelSkipWidth={ultraCompact ? 20 : compact ? 20 : 12}
          labelSkipHeight={ultraCompact ? 20 : compact ? 20 : 12}
          labelTextColor="#000000"
          axisTop={null}
          axisRight={null}
          axisBottom={(ultraCompact && !showXAxis) ? null : {
            tickSize: compact ? 0 : 5,
            tickPadding: compact ? 20 : 30,
            tickRotation: -45,
            legend: compact ? undefined : '',
            legendPosition: 'middle',
            legendOffset: compact ? 70 : 70,
            format: (value) => truncateText(String(value), compact ? 8 : 15),
            renderTick: ({ x, y, value, tickIndex }) => (
              <g 
                transform={`translate(${x},${y + 5})`}
                onMouseEnter={() => handleMouseEnter(String(value), x, y)}
                onMouseLeave={handleMouseLeave}
                style={{ cursor: 'pointer' }}
              >
                <text
                  textAnchor="end"
                  transform="rotate(-45)"
                  dominantBaseline="middle"
                  style={{
                    fill: '#333',
                    fontSize: ultraCompact ? 10 : compact ? 11 : 12,
                    fontWeight: hoveredItem === String(value) ? 'bold' : 'normal',
                    textDecoration: hoveredItem === String(value) ? 'underline' : 'none',
                  }}
                >
                  {truncateText(String(value), compact ? 8 : 15)}
                </text>
              </g>
            )
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: `${viewBy === 'product' ? 'Total Count' : viewBy === 'channel' ? 'Total Count' : 'Status'}`,
            legendPosition: 'middle',
            legendOffset: -40,
            format: value => {
              if (typeof value === 'number') {
                return value >= 1000000
                  ? `${Math.round(value / 1000000)}M`
                  : value >= 1000
                  ? `${Math.round(value / 1000)}k`
                  : value.toString();
              }
              return value;
            },
            tickValues: []
          }}
          enableGridY={false}
          enableGridX={false}
          legends={ultraCompact || compact ? [] : [
            {
              dataFrom: 'keys',
              anchor: 'bottom-right',
              direction: 'column',
              justify: false,
              translateX: 120,
              translateY: 0,
              itemsSpacing: 2,
              itemWidth: 100,
              itemHeight: 20,
              itemDirection: 'left-to-right',
              itemOpacity: 0.85,
              symbolSize: 20,
              symbolShape: 'square',
              itemTextColor: '#333333',
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemOpacity: 1
                  }
                }
              ]
            }
          ]}
          layers={[
            'grid',
            'axes',
            'bars',
            'markers',
            'legends',
            'annotations',
            // Empty custom layer - we're now showing metrics in the tooltip instead
            () => null
          ]}
          role="application"
          ariaLabel={`${viewBy === 'product' ? 'Product' : viewBy === 'channel' ? 'Channel' : 'Status'} bar chart`}
          barAriaLabel={e => `${e.id}: ${e.formattedValue} in ${e.indexValue}`}
          theme={chartTheme}
          onClick={handleClick}
          tooltip={({ id, indexValue, value }) => formatTooltipContent(id, indexValue, value, data)}
          animate={true}
          motionConfig="gentle"
        />

        {/* Tooltip for x-axis labels */}
        {hoveredItem && hoverPosition && (
          (() => {
            const metrics = getMetricsForItem(hoveredItem);
            return (
              <div
                style={{
                  position: 'absolute',
                  bottom: compact ? 80 : 140, // Adjust position to account for increased margin
                  left: hoverPosition.x, // Position directly above the hovered label
                  transform: 'translateX(-50%)', // Center the tooltip over the label
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                  padding: '10px 16px',
                  zIndex: 9999,
                  minWidth: '160px',
                  textAlign: 'center',
                  opacity: isVisible ? 1 : 0,
                  transition: 'opacity 0.3s ease-in-out',
                  pointerEvents: 'none' // Prevent the tooltip from interfering with mouse events
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: '5px', fontSize: '14px', color: '#333' }}>
                  {hoveredItem}
                </div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  gap: '10px',
                  borderTop: '1px solid #eee',
                  paddingTop: '8px',
                  marginTop: '3px' 
                }}>
                  <div style={{ fontSize: '12px', color: '#555' }}>Base:</div>
                  <div style={{ fontSize: '12px', fontWeight: 'bold' }}>
                    {metrics.base.toLocaleString()}
                  </div>
                </div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  gap: '10px',
                  marginTop: '3px' 
                }}>
                  <div style={{ fontSize: '12px', color: '#555' }}>Conv:</div>
                  <div style={{ fontSize: '12px', fontWeight: 'bold' }}>
                    {metrics.converted.toLocaleString()}
                  </div>
                </div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  gap: '10px',
                  marginTop: '3px',
                  color: '#007bff',
                  fontWeight: 'bold'
                }}>
                  <div style={{ fontSize: '12px' }}>Rate:</div>
                  <div style={{ fontSize: '12px' }}>
                    {metrics.rate.toFixed(1)}%
                  </div>
                </div>
              </div>
            );
          })()
        )}
      </CardContent>
    </Card>
  );
}; 